import clsx from 'clsx';
import { ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { noopFunction } from '@vestico/utils';

import { useElementSize } from '../hooks';
import style from './Carousel.module.css';
import { DragMove, Movement } from './DragMove';

export type CarouselArrowType = 'prev' | 'next';
export type IndexMoveOperation = 'increase' | 'decrease';

export type RenderArrowProps = {
  type: CarouselArrowType;
  onClick: () => void;
  isEdge: boolean;
};

const TRANSITION_DURATION_DEFAULT = 350;

export interface CarouselProps {
  direction?: 'vertical' | 'horizontal';
  initialIndex?: number;
  onWillAnimateToIndex?: (newIndex: number) => void;
  onDidAnimateToIndex?: (newIndex: number) => void;
  transitionDuration?: number;
  renderArrow?: (arrow: RenderArrowProps) => ReactNode;
  disableScrolling?: boolean;
  children: ReactNode[];
  gap?: number;
  itemLength: number;
  isRTL?: boolean;
  externalScroll?: Movement;
}

const DRAG_FLICK_TIME_THRESHOLD = 300;

export const Carousel = ({
  direction,
  initialIndex,
  children,
  onWillAnimateToIndex,
  onDidAnimateToIndex,
  transitionDuration,
  renderArrow,
  disableScrolling,
  gap,
  itemLength,
  isRTL,
  externalScroll,
}: CarouselProps) => {
  const [dragTranslate, setDragTranslate] = useState<number>(0);

  const [dragDirectionNext, setDragDirectionNext] = useState<boolean | undefined>(undefined);
  const [lastDragTimestamp, setLastDragTimestamp] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const useDimensions = useElementSize(rootRef);

  const [currentIndex, setCurrentIndex] = useState(initialIndex ?? 0);
  const itemCount = useMemo(() => children.length, [children.length]);

  const maxTranslation = useMemo(() => {
    const screenWidth = direction === 'horizontal' ? useDimensions.width : useDimensions.height;
    const totalWidth = itemCount * itemLength + (itemCount - 1) * (gap ?? 0);
    const maxTrans = Math.min(screenWidth - totalWidth, 0);
    return maxTrans;
  }, [itemLength, direction, useDimensions.width, useDimensions.height, itemCount, gap]);

  const maxIndexToScroll = useMemo(() => {
    if (maxTranslation === undefined || maxTranslation > 0) {
      return itemCount;
    }
    const overflowTranslation = Math.abs(maxTranslation);
    const extendedItemLegth = itemLength + (gap ?? 0);
    const overflowItems = Math.floor(overflowTranslation / extendedItemLegth);
    return overflowItems;
  }, [itemCount, gap, itemLength, maxTranslation]);

  const currentIndexIsLast = useMemo(
    () => currentIndex !== undefined && (currentIndex >= itemCount - 1 || currentIndex >= maxIndexToScroll),
    [currentIndex, itemCount, maxIndexToScroll]
  );
  const [animatingOutIndex, setAnimatingOutIndex] = useState<number | undefined>(undefined);

  const [dragging, setDragging] = useState(false);

  const handleDragMove = useCallback(
    (movement: Movement) => {
      const lastDragTranslate = dragTranslate;
      const newDragTranslate = direction === 'horizontal' ? movement.x : movement.y;

      const dragDiff = lastDragTranslate - newDragTranslate;
      // Don't trigger a change in drag direction if we only drag very little
      if (Math.abs(dragDiff) > 1) {
        const movingToNext = dragDiff > 0;
        setDragDirectionNext(movingToNext);
      }

      setLastDragTimestamp(Date.now());
      setDragTranslate(newDragTranslate);
    },
    [dragTranslate, direction]
  );

  const handlePointerDown = useCallback(() => {
    setDragTranslate(0);
    setDragging(true);
  }, []);

  const calculateDragTranslate = useCallback(() => {
    let adjustedTranslate = dragTranslate;
    if ((currentIndex === 0 && dragTranslate > 0) || (currentIndexIsLast && dragTranslate < 0)) {
      const absTranslate = Math.abs(dragTranslate);
      const constant = 0.55;
      const length = (direction === 'horizontal' ? useDimensions.width : useDimensions.height) ?? 0;
      adjustedTranslate = (1.0 - 1.0 / ((absTranslate * constant) / length + 1.0)) * length;
      if (dragTranslate < 0) {
        adjustedTranslate = -adjustedTranslate;
      }
    }
    return adjustedTranslate;
  }, [currentIndex, currentIndexIsLast, direction, dragTranslate, useDimensions.height, useDimensions.width]);

  const getTranslationForIndex = useCallback(
    (index: number) => {
      const indexTranslation = isRTL ? (maxTranslation ?? 0) + index * itemLength + index * (gap ?? 0) : -index * itemLength - index * (gap ?? 0);
      const newIndexTranslation = Math.min(Math.max(indexTranslation, maxTranslation ?? 0), 0);
      return newIndexTranslation;
    },
    [gap, isRTL, itemLength, maxTranslation]
  );

  const currentTranslation = useMemo(() => {
    let currentIndexTranslation = 0;
    if (currentIndex !== undefined) {
      currentIndexTranslation = getTranslationForIndex(currentIndex);
    }
    if (dragging) {
      const adjustedTranslate = calculateDragTranslate();
      return currentIndexTranslation + adjustedTranslate;
    }
    return currentIndexTranslation;
  }, [calculateDragTranslate, currentIndex, dragging, getTranslationForIndex]);

  const [dynamicTransitionDuration, setDynamicTransitionDuration] = useState(0);
  const animating = useMemo(() => animatingOutIndex !== undefined, [animatingOutIndex]);

  const moveToIndex = useCallback(
    (newIndex: number) => {
      setCurrentIndex(newIndex);

      // Update dynamic translation duration based on how much we still have to go
      // get the current translation
      const newTranslation = getTranslationForIndex(newIndex);

      const translationDiff = Math.abs(newTranslation - currentTranslation);
      const newDynamicTransitionDuration = (translationDiff / itemLength) * (transitionDuration ?? TRANSITION_DURATION_DEFAULT);
      setDynamicTransitionDuration(newDynamicTransitionDuration);

      if (onWillAnimateToIndex) {
        onWillAnimateToIndex(newIndex);
      }

      setAnimatingOutIndex(currentIndex);

      setTimeout(() => {
        requestAnimationFrame(() => {
          if (newIndex !== undefined && onDidAnimateToIndex) {
            onDidAnimateToIndex(newIndex);
          }
          setAnimatingOutIndex(undefined);
        });
      }, newDynamicTransitionDuration);
    },
    [currentIndex, currentTranslation, getTranslationForIndex, itemLength, onDidAnimateToIndex, onWillAnimateToIndex, transitionDuration]
  );

  const moveIndex = useCallback(
    (move: IndexMoveOperation) => {
      if (currentIndex === undefined) {
        return;
      }

      let newIndex: number | undefined;
      if (move === 'increase' && !currentIndexIsLast) {
        newIndex = currentIndex + 1;
      } else if (move === 'decrease' && currentIndex > 0) {
        newIndex = currentIndex - 1;
      }

      if (newIndex !== undefined) {
        moveToIndex(newIndex);
      }
    },
    [currentIndex, currentIndexIsLast, moveToIndex]
  );

  const stopDragging = useCallback(() => {
    setDragging(false);
    setDragTranslate(0);
    setDragDirectionNext(undefined);
  }, []);

  const handlePointerUp = useCallback(() => {
    if (currentIndex !== undefined) {
      const timeSinceLastDragOperation = Date.now() - lastDragTimestamp;
      const draggedSufficientlyFast = timeSinceLastDragOperation < DRAG_FLICK_TIME_THRESHOLD;

      let moveOperation: IndexMoveOperation | undefined;
      // If user flicked, use direction of drag

      if (draggedSufficientlyFast && Math.abs(dragTranslate) > 15) {
        if (dragDirectionNext && dragTranslate < 0) {
          moveOperation = 'increase';
        } else if (!dragDirectionNext && dragTranslate > 0) {
          moveOperation = 'decrease';
        }
      }
      // Otherwise calculate closest index
      else {
        const relativeDragDiff = dragTranslate / itemLength;

        if (relativeDragDiff < -0.5) {
          moveOperation = 'increase';
        } else if (relativeDragDiff > 0.5) {
          moveOperation = 'decrease';
        }
      }

      if (moveOperation !== undefined) {
        if (isRTL) {
          moveOperation = moveOperation === 'decrease' ? 'increase' : 'decrease';
        }

        moveIndex(moveOperation);
      }
    }

    stopDragging();
  }, [currentIndex, stopDragging, lastDragTimestamp, itemLength, dragDirectionNext, dragTranslate, isRTL, moveIndex]);

  const handlePointerCancel = useCallback(() => stopDragging(), [stopDragging]);

  const onPrevArrowClick = useCallback(() => moveIndex('decrease'), [moveIndex]);
  const onNextArrowClick = useCallback(() => moveIndex('increase'), [moveIndex]);

  useLayoutEffect(() => {
    if (!externalScroll) {
      return;
    }
    const { x, y } = externalScroll;
    const isDragging = x !== 0 || y !== 0;
    setDragging(isDragging);

    if (isDragging) {
      handleDragMove(externalScroll);
    } else {
      handlePointerUp();
    }
    // Only observe external scroll here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalScroll]);

  const prevArrow = useMemo(() => {
    if (!renderArrow) {
      return null;
    }

    const indexToUse = animatingOutIndex !== undefined ? animatingOutIndex : currentIndex;
    return renderArrow({ type: 'prev', onClick: onPrevArrowClick, isEdge: indexToUse === 0 });
  }, [animatingOutIndex, currentIndex, onPrevArrowClick, renderArrow]);

  const nextArrow = useMemo(() => {
    if (!renderArrow) {
      return null;
    }
    const indexToUse = animatingOutIndex !== undefined ? animatingOutIndex : currentIndex;
    return renderArrow({ type: 'next', onClick: onNextArrowClick, isEdge: indexToUse === undefined || indexToUse >= maxIndexToScroll });
  }, [renderArrow, animatingOutIndex, currentIndex, onNextArrowClick, maxIndexToScroll]);

  useEffect(
    () => {
      // Ignore if we've been dragging recently
      if (Date.now() - lastDragTimestamp < (transitionDuration ?? TRANSITION_DURATION_DEFAULT) || animating) {
        return noopFunction;
      }

      if (currentIndex !== initialIndex && initialIndex !== undefined) {
        const id = requestAnimationFrame(() => {
          setCurrentIndex(initialIndex);
        });
        return () => cancelAnimationFrame(id);
      }
      return noopFunction;
    },
    // Purposefully only observe changes to initialIndex here
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentIndex, initialIndex, lastDragTimestamp]
  );

  useEffect(() => {
    if (!disableScrolling) {
      return;
    }
    stopDragging();
  }, [disableScrolling, stopDragging]);

  const finalTransitionDuration = useMemo(() => {
    if (!dragging && dynamicTransitionDuration) {
      return dynamicTransitionDuration;
    }
    return transitionDuration;
  }, [dragging, dynamicTransitionDuration, transitionDuration]);

  const listRef = useRef<HTMLDivElement | null>(null);

  const [isAppearing, setAppearing] = useState(true);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      setAppearing(false);
    });
    return () => cancelAnimationFrame(id);
  }, []);

  useLayoutEffect(
    () => {
      const animationID = requestAnimationFrame(() => {
        const { current: list } = listRef;
        if (!list) {
          return;
        }

        // use 10ms as fallback to cause less wiggling
        const notAnimated = dragging || isAppearing || !animating;
        list.style.transitionDuration = notAnimated ? '10ms' : `${finalTransitionDuration}ms`;
        list.style.transitionTimingFunction = notAnimated ? 'linear' : 'ease-out';
        const transformInPx = typeof currentTranslation === 'string' ? currentTranslation : `${currentTranslation}px`;
        list.style.transform = direction === 'horizontal' ? `translateX(${transformInPx})` : `translateY(${transformInPx})`;
      });
      return () => cancelAnimationFrame(animationID);
    },
    // Purposefully don't observe changes to isAppearing as it'd cause a rerender too early
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dragging, finalTransitionDuration, currentTranslation, direction, animating]
  );

  return (
    <div className={clsx(style.root, { [style.horizontal]: direction === 'horizontal' })} ref={rootRef}>
      <DragMove
        key="carousel"
        onDragMove={handleDragMove}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        draggingEnabled={!disableScrolling}
        blockEvents
      >
        <div
          className={clsx(style.list, { [style.rtl]: isRTL })}
          style={{
            gap: direction === 'horizontal' ? gap : undefined,
          }}
          ref={listRef}
        >
          {children}
        </div>
      </DragMove>
      {prevArrow || nextArrow ? (
        <div className={clsx(style.arrows)}>
          {prevArrow}
          {nextArrow}
        </div>
      ) : null}
    </div>
  );
};

Carousel.defaultProps = {
  transitionDuration: TRANSITION_DURATION_DEFAULT,
};
